import { createContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LabelValue } from "types";
import useExercises from "../useExercises";
import useNaturalSort from "hooks/useNaturalSort";

export interface EquipmentsContextType {
  equipmentList: LabelValue[];
}

export const EquipmentsContext = createContext<LabelValue[] | undefined>(undefined);
EquipmentsContext.displayName = "EquipmentsContext";

export interface EquipmentsContextActionsType {
  getEquipmentValuesFromList: (equipments: LabelValue[]) => string[];
  filterEquipment: (equipment: string[]) => LabelValue[];
}

export const EquipmentsActionsContext = createContext<EquipmentsContextActionsType | undefined>(undefined);
EquipmentsActionsContext.displayName = "EquipmentsActionsContext";

interface EquipmentsContextProviderProps {
  children?: React.ReactNode;
}

function EquipmentsProvider(props: EquipmentsContextProviderProps) {
  const { t } = useTranslation();
  const exercises = useExercises();
  const naturalCompare = useNaturalSort();

  const allEquipment = useMemo(
    () => Array.from(new Set(exercises.map((exercise) => exercise.equipment).flat())).sort(naturalCompare),
    [exercises, naturalCompare]
  );

  /** List of all equipment with translated label and value */
  const equipmentList = useMemo(
    () =>
      allEquipment.map((equip) => ({
        label: t("exercise_equipment", { context: equip, defaultValue: equip }),
        value: equip,
      })),
    [allEquipment, t]
  );

  /** Parse equipment list to values */
  const getEquipmentValuesFromList = useCallback((equipments: Array<LabelValue>) => {
    return equipments.map((equipment) => equipment.value);
  }, []);

  /** Filter equipment list by equipments, used to show the translated values */
  const filterEquipment = useCallback(
    (equipment: string[]) => {
      return equipmentList.filter((equip) => equipment.includes(equip.value));
    },
    [equipmentList]
  );

  const actions = useMemo(
    () => ({
      getEquipmentValuesFromList,
      filterEquipment,
    }),
    [getEquipmentValuesFromList, filterEquipment]
  );
  return (
    <EquipmentsActionsContext.Provider value={actions}>
      <EquipmentsContext.Provider value={equipmentList}>{props.children}</EquipmentsContext.Provider>
    </EquipmentsActionsContext.Provider>
  );
}

export default EquipmentsProvider;
